import React, { Fragment, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  AlertTriangle as AlertTriangleIcon,
  Clock as ClockIcon,
  Info as InfoIcon,
  Layers as LayersIcon,
} from 'react-feather'

import { sortBy, isEmpty, size } from 'lodash'

import {
  HelpCircle as HelpCircleIcon,
  PlusCircle as PlusCircleIcon,
} from 'react-feather'
import {
  Box,
  Card,
  Container,
  Grid,
  TablePagination,
  useMediaQuery,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Typography,
  Paper,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { useTheme } from '@material-ui/styles'

import {
  Page,
  Filters,
  FilterButton,
  DriveTour,
  Permitted,
  ContentHeader,
  BaseStatusCard,
  TutorialTrails,
} from 'components'
import { QuestionTable } from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'

import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const QuestionControlsMain = () => {
  const {
    QUESTION_CONTROL_DRIVER_STEPS_OBJECT,
    QUESTION_CONTROL_DRIVER_STEP_1,
    QUESTION_CONTROL_DRIVER_STEP_2,
    QUESTION_CONTROL_DRIVER_STEP_3,
    KIND,
    STATUSES,
    STATUS_IDS,
    STATUS_LABELS,
    SECOND_STEP_ANSWER_ITEMS,
    LEVEL_CONFIGS,
  } = constants.questionControl

  const { handleStatusCard } = helpers.baseStatusCard

  const [filterFields, setFilterFields] = useState({})
  const [openTour, setOpenTour] = useState(false)
  const [categories, setCategories] = useState([])
  const [step, setStep] = useState(0)

  const history = useHistory()

  const filter = useFilter()
  const classes = useStyles()
  const theme = useTheme()

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const [currentTab, setCurrentTab] = useState(
    filter?.filters?.questionnaireKind || KIND.ALL,
  )

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.questionControls.list,
    {
      notStatus: STATUS_IDS.INACTIVE_STATUS,
      page,
      perPage,
      ...filter.filters,
    },
    [page, perPage, filter.filters],
  )

  const questionControls = response?.data?.questionControls

  const {
    response: statsResponse,
    isLoading: statsIsLoading,
    refresh: statsRefresh,
  } = useFetch(service.dponet.questionControls.stats, {}, [])

  const stats = statsResponse?.data?.stats

  const STATUS_CARD_ITEMS = [
    {
      status: STATUS_IDS.WAITING_ANSWER_STATUS,
      title: `${stats?.waitingAnswer} Questões`,
      subTitle: STATUS_LABELS.WAITING_ANSWER_STATUS_LABEL + 's',
      Icon: ClockIcon,
      iconBgColor: theme.palette.yellowWarningLight,
    },
    {
      status: STATUS_IDS.PENDING_STATUS,
      title: `${stats?.pending} Medidas`,
      subTitle: STATUS_LABELS.PENDING_STATUS_LABEL + 's',
      Icon: InfoIcon,
      iconBgColor: theme.palette.warning.main,
    },
    {
      status: STATUS_IDS.OVERDUE_STATUS,
      title: `${stats?.overdue} Planos`,
      subTitle: STATUS_LABELS.OVERDUE_STATUS_LABEL + 's',
      Icon: AlertTriangleIcon,
      iconBgColor: theme.palette.error.main,
    },
    {
      status: STATUS_IDS.ADOPTED_STATUS,
      title: `${stats?.adopted} Controles`,
      subTitle: STATUS_LABELS.ADOPTED_STATUS_LABEL + 's',
      Icon: LayersIcon,
      iconBgColor: theme.palette.success.light,
    },
  ]

  const getStepDescription = (config, concluded) => {
    if (concluded && config.concludedDescription)
      return config.concludedDescription

    if (config.description) return config.description

    if (config.descriptionListContent)
      return (
        <Fragment>
          <Typography>{config.descriptionListContent}</Typography>
          <Box component="ul" ml={3}>
            {config.descriptionListItems?.map((item, index) => (
              <Typography key={index} component="li">
                {item}
              </Typography>
            ))}
          </Box>
        </Fragment>
      )

    return []
  }

  const firstStepCompleted = stats?.waitingAnswer === 0

  const secondStepCompleted = SECOND_STEP_ANSWER_ITEMS.every(
    (status) => stats?.[helpers.changeCase.methods.camelCase(status)] === 0,
  )

  const stepConditions = [firstStepCompleted, secondStepCompleted, false]

  const TUTORIAL_TRAILS_STEPS = [1, 2, 3].map((level) => {
    const config = LEVEL_CONFIGS[level]
    const concluded = stepConditions[level - 1]

    return {
      title: config.title,
      description: getStepDescription(config, concluded),
      concluded,
    }
  })

  const handleStepChange = (stepIndex) => setStep(stepIndex)

  const handleChangeTabs = (_, value) => {
    setCurrentTab(value)

    if (value === KIND.ALL) {
      const { questionnaireKind, ...restFilters } = filter.filters
      filter.setFilters(restFilters)
    } else {
      filter.setFilters({ ...filter.filters, questionnaireKind: value })
    }
  }

  const handleRequestRefresh = () => {
    refresh()
    statsRefresh()
  }

  const validationSteps = () => {
    if (questionControls === undefined) return
    if (!isLoading) {
      const steps = [...QUESTION_CONTROL_DRIVER_STEPS_OBJECT]
      const statusSet = new Set()

      questionControls.forEach((question) => {
        const status = question?.status

        if (status && !statusSet.has(status)) {
          const stepObject = helpers.questionControls.statusDriverSteps(status)
          if (stepObject) {
            steps.push(stepObject)
            statusSet.add(status)
          }
        }
      })

      const sortedSteps = sortBy(steps, 'element')

      return sortedSteps
    }
    return []
  }

  const handleOpenTour = () => {
    const where = 'Medidas de Segurança'
    const title = 'Tour'

    const params = {
      action: 'tour-medidas-de-segurança-diagnostico',
    }

    helpers.mixpanel.track(where, title, params)
    setOpenTour(true)
  }

  const handleNew = () => history.push(routes.questionControls.new)

  useEffect(() => {
    const loadCategories = async () => {
      if (isEmpty(categories)) {
        const response = await service.dponet.questionControls.listCustomKinds()

        setCategories(response?.data?.questionControls)
      }
    }
    loadCategories()

    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    const notConcludedIndex = TUTORIAL_TRAILS_STEPS.findIndex(
      (step) => !step.concluded,
    )

    const newStep =
      notConcludedIndex === -1
        ? size(TUTORIAL_TRAILS_STEPS) - 1
        : notConcludedIndex

    setStep(newStep)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats])

  useEffect(() => {
    setFilterFields({ ...filterFields, status: filter.filters?.status })
  }, [filter.filters?.status])

  return (
    <Page title="Todos as medidas de segurança">
      <Container maxWidth={false} className={classes.container}>
        <ContentHeader title="Medidas de segurança">
          <Tooltip title="Iniciar o tour guiado">
            <Button
              id={QUESTION_CONTROL_DRIVER_STEP_1}
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
          <Permitted tag={constants.permissions.DIAGNOSTICS.UPDATE}>
            <Button
              id={QUESTION_CONTROL_DRIVER_STEP_2}
              variant="contained"
              color="primary"
              startIcon={<PlusCircleIcon size={20} />}
              onClick={handleNew}
              fullWidth
            >
              Adicionar medida
            </Button>
          </Permitted>
          <FilterButton setDrawerOpen={filter.setDrawerOpen} fullWidth />
        </ContentHeader>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <Box
            id={QUESTION_CONTROL_DRIVER_STEP_3}
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(4)}
          >
            <Paper variant="outlined" component={Box} p={2}>
              <TutorialTrails
                steps={TUTORIAL_TRAILS_STEPS}
                currentStep={step}
                onChange={handleStepChange}
                iframe="https://player.vimeo.com/video/1098295454?badge=0&amp;autopause=0"
                isLoading={statsIsLoading}
              />
            </Paper>
            <Typography variant="h4" color="primary">
              Filtre por status
            </Typography>
            <Grid container spacing={2}>
              {STATUS_CARD_ITEMS.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <BaseStatusCard
                    {...item}
                    selected={filter.filters?.status === item?.status}
                    onClick={() =>
                      handleStatusCard({ filter, value: item?.status })
                    }
                    isLoading={statsIsLoading}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box>
            <Tabs
              scrollButtons="auto"
              onChange={handleChangeTabs}
              textColor="primary"
              indicatorColor="primary"
              value={currentTab || KIND.ALL}
              className={classes.borderBottom}
              variant={'scrollable'}
            >
              <Tab
                key={KIND.ALL}
                value={KIND.ALL}
                label={<Box>Todos</Box>}
                wrapped
              />
              {categories.map((category) => (
                <Tab
                  key={category.id}
                  value={category.id}
                  label={
                    <Box className={classes.categoriesText}>
                      {category.name}
                    </Box>
                  }
                  wrapped
                />
              ))}
            </Tabs>
            <Card>
              <Box display="flex" width="100%">
                <Grid container>
                  <QuestionTable
                    questionControls={questionControls}
                    refresh={handleRequestRefresh}
                    isLoading={isLoading}
                  />
                </Grid>
              </Box>

              <Box px={2} display="flex" justifyContent="flex-end">
                {!isLoading && response ? (
                  <TablePagination
                    component="div"
                    count={response?.data?.meta?.totalCount}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    page={page - 1}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[5, 10, 25, 100]}
                    labelRowsPerPage={isDesktop ? 'Por página' : ''}
                    nextIconButtonProps={{ size: 'small' }}
                    backIconButtonProps={{ size: 'small' }}
                  />
                ) : (
                  <Box py="11px">
                    <Skeleton variant="rect" width={200} height={30} />
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </Box>
        <Filters
          filter={filter}
          defaultOrder={constants.orderFilter.OLDER_CREATED}
        >
          <input textfieldinput="true" label="Identificador" name="id" />
          <input
            textfieldinput="true"
            label="Medida de segurança"
            name="title"
          />
          <select
            textfieldinput="true"
            label="Status"
            name="status"
            value={filterFields?.status || ''}
            onChange={(e) =>
              setFilterFields({ ...filterFields, status: e.target.value })
            }
          >
            <option value=""></option>
            {STATUSES.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          <input
            datepickerinput="true"
            label="Prazo maior que"
            name="deadlineDateGt"
          />
          <input
            datepickerinput="true"
            label="Prazo menor que"
            name="deadlineDateLt"
          />
        </Filters>
      </Container>

      <DriveTour
        stepsMatrix={validationSteps()}
        customStyle={classes.customPopover}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default QuestionControlsMain
